import { Button, ButtonProps, ButtonVariant, CityState, ClickEvent, Panel, PanelProps, Table, TableRow, TableSelectionEvent, Textbox } from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { McLeodClassicIntegration, ModelRow } from "@mcleod/core";
import { OrderTabView } from "../OrderTabView";
import { MobileCommPositionTable } from "./MobileCommPositionTable";

interface ActionButtonProps {
    button: Button;
    /**
     * If the shouldEnable() function is provided, it will be called when the table selection changes.
     * The button will be enabled or disabled based off the return value of this function.
     * If shouldEnable() is not provided, the button will always be enabled when a row is selected.
     * @param modelRow
     */
    shouldEnable?: (table: Table, modelRow: ModelRow) => boolean;
}

const buttonProps: Partial<ButtonProps> = {
    color: "primary", variant: ButtonVariant.round, borderWidth: 0,
    imageWidth: 22, imageHeight: 22, rowBreak: false, enabled: false
}

const borderPanelProps: Partial<PanelProps> = {
    height: 20,
    padding: 0,
    marginRight: 10,
    borderRightWidth: 1,
    borderRightColor: "subtle.light",
    fillRow: true,
    rowBreak: false
}

/**
 * There are 3 steps to adding a new action button:
 * 1. Add a new ActionButtonType enum value.
 * 2. Create a new function that will return the button.
 * 3. Using the new enum value and the function from step 2, create a new ActionButtonProps object and add it to the
 *   buttonProps map in the buildButtonMap() function.  See the comment in ActionButtonProps interface for more information.
 */

export enum ActionButtonType {
    FINDNEAR,
    DISPATCH,
    REEFER_COMMANDS,
    REEFER_ALARMS
}

export class MobileCommPositionToolPanel extends Panel {
    private layout: MobileCommPositionTable;
    private table: Table;
    private buttonMap: Map<ActionButtonType, ActionButtonProps>;
    private buttonTypes: ActionButtonType[];

    constructor(layout: MobileCommPositionTable, ...buttonsTypes: ActionButtonType[]) {
        super({ marginLeft: 15, paddingTop: 0, paddingBottom: 0 });

        this.layout = layout;
        this.table = layout.tableMobileCommPosition;
        this.buttonTypes = buttonsTypes;
        this.configureToolPanel();
        if (buttonsTypes.length > 0) {
            this.buildButtonMap();
            this.addActionButtons();
            this.table.addSelectionListener((event: TableSelectionEvent) => this.onSelectionChanged(event));
        }
        this.table.addCenterTool(this);
    }

    private onSelectionChanged(event: TableSelectionEvent) {
        const modelRow = event.newRow?.data as ModelRow;
        this.table.selectedRows.forEach(row => {
            row.backgroundColor = this.layout.isFindNearData ? "success.lightest" : "primary.lightest";
        });

        this.buttonTypes.forEach(buttonType => {
            const props = this.buttonMap.get(buttonType);
            if (props.shouldEnable != null) {
                props.button.enabled = props.shouldEnable(this.table, modelRow);
            } else {
                props.button.enabled = true;
            }
        })
    }

    private configureToolPanel() {
        this.table.overrideUrlParams = (selectedRow: TableRow) => {
            return { orderId: selectedRow.data.get("order_id"), movementId: selectedRow.data.get("movement_id") }
        };
    }

    /**
     * When adding a new action button, add it to the buttonProps map here.
     * @private
     */
    private buildButtonMap() {
        this.buttonMap = new Map();
        this.buttonMap.set(ActionButtonType.FINDNEAR, {
            button: this.getFindNearButton(), shouldEnable(table, modelRow) {
                return true;
            }
        });

        this.buttonMap.set(ActionButtonType.DISPATCH, {
            button: this.getDispatchButton(), shouldEnable(table, modelRow) {
                const selectedRows = table.selectedRows;
                let showDispatch = false;
                if (selectedRows.length > 0) {
                    selectedRows.forEach(row => {
                        if (row.data.get("movement_id") && row.data.get("order_id")) {
                            showDispatch = true;
                        }
                    })
                }
                return showDispatch;
            }
        });

        this.buttonMap.set(ActionButtonType.REEFER_COMMANDS, {
            button: this.getReeferCommandsButton(), shouldEnable(table, modelRow) {
                const selectedRows = table.selectedRows;
                let enableButton = true;

                if (selectedRows.length == 1) {
                    if (modelRow.get("mc_unit_type") != "L") {
                        enableButton = false;
                    }

                    if (modelRow.get("reefer_id") === undefined) {
                        enableButton = false;
                    }

                    const controlID = modelRow.get("mc_control_id") as string;
                    if (controlID && !controlID.startsWith("ORB")) {
                        enableButton = false;
                    }
                } else {
                    enableButton = false;
                }

                return enableButton;
            }
        });

        this.buttonMap.set(ActionButtonType.REEFER_ALARMS, {
            button: this.getReeferAlarmsButton(), shouldEnable(table, modelRow) {
                const selectedRows = table.selectedRows;
                let enableButton = true;

                if (selectedRows.length == 1) {
                    if (modelRow.get("mc_unit_type") != "L") {
                        enableButton = false;
                    }

                    if (modelRow.get("has_reefer_tracking") === false) {
                        enableButton = false;
                    }

                    const controlID = modelRow.get("mc_control_id") as string;
                    if (controlID &&
                        (!controlID.startsWith("ORB") && !controlID.startsWith("IBR") &&
                            !controlID.startsWith("TK") && !controlID.startsWith("BLU") && !controlID.startsWith("PA"))) {
                        enableButton = false;
                    }
                } else {
                    enableButton = false;
                }

                return enableButton;
            }
        });
    }

    /**
     * This adds the action buttons to the panel.
     */
    async addActionButtons() {
        this.add(new Panel({ ...borderPanelProps, marginLeft: 0, marginRight: 10 }));
        this.buttonTypes.forEach(buttonType => this.add(this.buttonMap.get(buttonType).button));
        this.add(new Panel({ ...borderPanelProps, marginLeft: 10, marginRight: 0 }));
        if (this.components.length == 2) {
            this.removeAll();
        }
    }

    public getButton(buttonType: ActionButtonType): Button {
        return this.buttonMap.get(buttonType).button;
    }

    private getFindNearButton(): Button {
        const buttonFindNear = new Button({
            ...buttonProps,
            id: "buttonFindNear",
            imageName: ImageName.viaPoint,
            imageHeight: 17,
            imageWidth: 17,
            tooltip: "FindNear",
            enabled: true,
        });
        buttonFindNear.addClickListener((event: ClickEvent) => this.findNearOnClick(event, buttonFindNear));
        return buttonFindNear;
    }

    private getDispatchButton(): Button {
        const buttonDispatch = new Button({
            ...buttonProps,
            id: "buttonDispatch",
            imageName: ImageName.truck,
            enabled: false,
            tooltip: "Current Movement"
        });
        buttonDispatch.addClickListener((event: ClickEvent) => this.dispatchOnClick(event));
        return buttonDispatch;
    }

    private getReeferCommandsButton(): Button {
        const buttonReeferCommands = new Button({
            ...buttonProps,
            id: "buttonCommand",
            imageName: ImageName.reeferStatus,
            enabled: false,
            tooltip: "Please select a position to see available reefer commands."
        });
        buttonReeferCommands.addClickListener((event: ClickEvent) => this.reeferCommandsOnClick(event));
        return buttonReeferCommands;
    }

    private getReeferAlarmsButton(): Button {
        const buttonReeferAlarms = new Button({
            ...buttonProps,
            id: "buttonAlarm",
            imageName: ImageName.alarm,
            enabled: false,
            tooltip: "Please select a position to check reefer alarms."
        });
        buttonReeferAlarms.addClickListener((event: ClickEvent) => this.reeferAlarmsOnClick(event));
        return buttonReeferAlarms;
    }

    private findNearOnClick(event: ClickEvent, findNearIcon: Button) {
        const textboxRadius = this.layout.textboxRadius;
        const textboxCity = this.layout.textboxCity;
        const buttonFindNear = this.layout.buttonFindNear;
        textboxRadius.visible = !textboxRadius.visible;
        textboxCity.visible = !textboxCity.visible;
        buttonFindNear.visible = !buttonFindNear.visible;

        findNearIcon.color = textboxRadius.visible ? "success" : "primary";
        this.layout.setFindNearColumnVisible(textboxRadius.visible);
        if (!textboxRadius.visible) {
            this.layout.searchList();
        } else {
            textboxRadius.clear();
            textboxCity.clearAllFields()
            this.table.data.forEach(data => data.set("find_near_dist", ""));
        }
    }

    private dispatchOnClick(event: ClickEvent) {
        const selectedRow = this.table.selectedRow;
        const selectedRows = this.table.selectedRows;
        selectedRows.forEach(selectedRow => {
            if (selectedRow.data.get("movement_id") && selectedRow.data.get("order_id")) {
                OrderTabView.navigateTo({ key: selectedRow.data.get("movement_id"), orderId: selectedRow.data.get("order_id"), newTab: true });
            }
        })
    }

    private reeferCommandsOnClick(event: ClickEvent) {
        const selectedRow = this.table.selectedRow;
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.mc.PanelMcTrailerReeferSettings", selectedRow.data.get("mc_unit_no"));
    }

    private reeferAlarmsOnClick(event: ClickEvent) {
        const selectedRow = this.table.selectedRow;
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.mc.EntryMcAlarmHistory", selectedRow.data.get("mc_unit_no"));
    }
}
